import React, { useState } from "react";
import { fetchWorkspaceStats } from "../../../actions/stats";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { Tooltip, IconButton } from "@material-ui/core";
import qs from "querystring";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import DeleteConfirmationDialog from "../DeleteMonitoredUriConfirmationDialog";
import apiFetch from "../../util/fetch-utils";
import { MONITORED_URI_DELETED } from "../../../actions/monitoredUri";
import { toaster } from "evergreen-ui";
import styled from "styled-components";

/* ── Table shell ──────────────────────────────────────────── */

const TableWrap = styled.div`
    border-radius: 14px;
    border: 1px solid #f0f0f0;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0,0,0,.06);
    background: #fff;
`;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
`;

const THead = styled.thead`
    background: #fafafa;
    border-bottom: 1px solid #f0f0f0;
`;

const Th = styled.th`
    padding: 10px 14px;
    font-size: 0.72rem;
    font-weight: 700;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Tr = styled.tr`
    border-bottom: 1px solid #f9fafb;
    transition: background 100ms ease;
    &:last-child { border-bottom: none; }
    &:hover { background: #f8f7ff; }
    &:hover .delete-btn { opacity: 1; }
`;

const Td = styled.td`
    padding: 10px 14px;
    font-size: 0.85rem;
    color: #374151;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
    cursor: ${({ $editable }) => ($editable ? "pointer" : "default")};
`;

/* ── URL cell ─────────────────────────────────────────────── */

const UrlCell = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
`;

const UrlLink = styled.a`
    color: #4f46e5;
    text-decoration: none;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1 1 auto;
    min-width: 0;
    &:hover { text-decoration: underline; }
`;

/* ── Badges ───────────────────────────────────────────────── */

const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 2px 7px;
    border-radius: 6px;
    font-size: 0.68rem;
    font-weight: 700;
    white-space: nowrap;
    flex-shrink: 0;
    background: ${({ $bg }) => $bg || "#f3f4f6"};
    color: ${({ $color }) => $color || "#374151"};
    ${({ $clickable }) => $clickable && `
        cursor: pointer;
        &:hover { filter: brightness(0.93); }
    `}
`;

const Arrow = styled.span`
    font-size: 0.65rem;
    color: #d1d5db;
    flex-shrink: 0;
`;

const MoreBtn = styled.button`
    background: none;
    border: 1px solid #e2e8f0;
    border-radius: 5px;
    padding: 1px 6px;
    font-size: 0.65rem;
    font-weight: 700;
    color: #6366f1;
    cursor: pointer;
    flex-shrink: 0;
    &:hover { background: #eef2ff; }
`;

const DeleteBtn = styled(IconButton)`
    && {
        opacity: 0;
        transition: opacity 100ms ease;
        padding: 4px;
    }
`;

const Muted = styled.span`
    color: #d1d5db;
`;

/* ── Helpers ──────────────────────────────────────────────── */

function statusStyle(code) {
    const n = parseInt(code, 10);
    if (!n)      return { bg: "#f3f4f6", color: "#6b7280" };
    if (n < 300) return { bg: "#dcfce7", color: "#15803d" };
    if (n < 400) return { bg: "#dbeafe", color: "#1d4ed8" };
    if (n < 500) return { bg: "#fee2e2", color: "#dc2626" };
    return { bg: "#fce7f3", color: "#9d174d" };
}

const MAX_HOPS = 3;

function RedirectChainCell({ chain }) {
    const [expanded, setExpanded] = useState(false);

    if (!chain.length) return <Muted>—</Muted>;

    const visible  = expanded ? chain : chain.slice(0, MAX_HOPS);
    const overflow = chain.length - MAX_HOPS;

    return (
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
            {visible.map((hop, i) => {
                const { bg, color } = statusStyle(hop.httpStatus);
                return (
                    <React.Fragment key={i}>
                        {i > 0 && <Arrow>→</Arrow>}
                        <Chip $bg={bg} $color={color}>{hop.httpStatus}</Chip>
                    </React.Fragment>
                );
            })}
            {!expanded && overflow > 0 && (
                <MoreBtn onClick={e => { e.stopPropagation(); setExpanded(true); }}>+{overflow}</MoreBtn>
            )}
        </div>
    );
}

function DiscoveredViaCell({ lastCrawl, onFilterClick }) {
    if (!lastCrawl) return <Muted>—</Muted>;

    const ahref    = lastCrawl.inboundLinksCount?.internal?.ahref;
    const sitemaps = lastCrawl.inboundLinks?.internal?.SITEMAP || [];
    const hasCrawl   = ahref > 0;
    const hasSitemap = sitemaps.length > 0;

    if (!hasCrawl && !hasSitemap) return (
        <Tooltip title="Filter: Orphan pages" placement="top">
            <Chip $bg="#fef9c3" $color="#92400e" $clickable onClick={() => onFilterClick({ isOrphan: "true" })}>Orphan</Chip>
        </Tooltip>
    );

    return (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {hasCrawl && (
                <Tooltip title="Filter: Missing in sitemap" placement="top">
                    <Chip $bg="#f0fdf4" $color="#15803d" $clickable onClick={() => onFilterClick({ missingInSitemap: "true" })}>Crawl</Chip>
                </Tooltip>
            )}
            {hasSitemap && (
                <Tooltip title="Filter: Missing in crawl" placement="top">
                    <Chip $bg="#eff6ff" $color="#1d4ed8" $clickable onClick={() => onFilterClick({ missingInCrawl: "true" })}>Sitemap{sitemaps.length > 1 ? ` · ${sitemaps.length}` : ""}</Chip>
                </Tooltip>
            )}
        </div>
    );
}

function CrawlScopeCell({ scope, onFilterClick }) {
    if (scope === "INTERNAL") return (
        <Tooltip title="Filter: Internal" placement="top">
            <Chip $bg="#f0fdf4" $color="#15803d" $clickable onClick={() => onFilterClick({ crawlScope: "internal" })}>Internal</Chip>
        </Tooltip>
    );
    if (scope === "EXTERNAL") return (
        <Tooltip title="Filter: External" placement="top">
            <Chip $bg="#eff6ff" $color="#1d4ed8" $clickable onClick={() => onFilterClick({ crawlScope: "external" })}>External</Chip>
        </Tooltip>
    );
    return <Chip $bg="#f3f4f6" $color="#6b7280">Unknown</Chip>;
}

/* ── Row ──────────────────────────────────────────────────── */

function UriRow({ row, workspaceNumber, onDelete, history, location }) {
    const cv        = row.currentValue;
    const chain     = cv?.redirectChainElements || [];
    const scope     = row.crawlScope;
    const lastCrawl = row.lastCrawl;

    const handleFilterClick = (params) => {
        const current = qs.parse(location.search.replace(/^\?/, ""));
        const next = { ...current, ...params };
        delete next.page;
        history.push("?" + qs.stringify(next));
    };

    return (
        <Tr>
            <Td style={{ width: "42%" }}>
                <UrlCell>
                    <Tooltip title={row.uri} placement="top-start">
                        <UrlLink href={"/#/ws/" + workspaceNumber + "/monitored-uris/" + row.id}>{row.uri}</UrlLink>
                    </Tooltip>
                </UrlCell>
            </Td>

            <Td style={{ width: "22%" }}>
                <RedirectChainCell chain={chain} />
            </Td>

            <Td style={{ width: "20%" }}>
                <DiscoveredViaCell lastCrawl={lastCrawl} onFilterClick={handleFilterClick} />
            </Td>

            <Td style={{ width: "11%" }}>
                <CrawlScopeCell scope={scope} onFilterClick={handleFilterClick} />
            </Td>

            <Td style={{ width: "5%", textAlign: "right", padding: "4px 10px" }}>
                <Tooltip title="Delete URL">
                    <DeleteBtn className="delete-btn" size="small" onClick={() => onDelete(row)}>
                        <DeleteIcon style={{ fontSize: 18, color: "#ef4444" }} />
                    </DeleteBtn>
                </Tooltip>
            </Td>
        </Tr>
    );
}

/* ── Main class component (keeps Redux connect) ───────────── */

class UriTable extends React.Component {
    state = { deleteDialog: { isOpen: false } };

    deleteConfirmationDialogCallback = (confirmed, entity) => {
        this.setState({ deleteDialog: { isOpen: false } });
        if (!confirmed) return;

        apiFetch("/api/monitored-uris/" + entity.id, { method: "DELETE" }).then((res) => {
            if (res.status === 204) {
                toaster.success("Deleted: " + entity.uri, { duration: 3 });
                this.props.dispatch({ type: MONITORED_URI_DELETED, id: entity.id });
                this.props.dispatch(fetchWorkspaceStats(this.props.match.params.workspaceNumber));
            } else {
                toaster.danger("Delete failed: " + entity.uri, {
                    description: res.status + " " + res.statusText,
                    duration: 10,
                });
            }
        });
    };

    openDelete = (entity) => this.setState({ deleteDialog: { isOpen: true, entity } });

    render() {
        const workspaceNumber = this.props.match.params.workspaceNumber;
        return (
            <div>
                <DeleteConfirmationDialog
                    {...this.state.deleteDialog}
                    onClose={this.deleteConfirmationDialogCallback}
                />
                <TableWrap>
                    <StyledTable>
                        <THead>
                            <tr>
                                <Th style={{ width: "42%" }}>URL</Th>
                                <Th style={{ width: "22%" }}>Redirect chain</Th>
                                <Th style={{ width: "20%" }}>Discoverability</Th>
                                <Th style={{ width: "11%" }}>Crawl scope</Th>
                                <Th style={{ width: "5%" }} />
                            </tr>
                        </THead>
                        <tbody>
                            {this.props.tableData.map((row, i) => (
                                <UriRow
                                    key={row.id ?? i}
                                    row={row}
                                    workspaceNumber={workspaceNumber}
                                    onDelete={this.openDelete}
                                    history={this.props.history}
                                    location={this.props.location}
                                />
                            ))}
                        </tbody>
                    </StyledTable>
                </TableWrap>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return { ...state.monitoredUri };
}

function mapDispatchToProps(dispatch) {
    return { dispatch };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(UriTable));
